import React, { useRef, useState } from "react";
import { Trash2 } from "lucide-react";

/**
 * SwipeRow — a row wrapper that reveals a destructive "Delete" action
 * via either:
 *   - swipe-left gesture (touch / pointer-drag)
 *   - long-press (600ms hold)
 *   - explicit ⋯ button (rendered by the parent)
 *
 * Renders children left-justified. On reveal, slides children left by
 * 84px exposing a red Delete button on the right.
 *
 * Props:
 *   onDelete — called when the user taps the revealed Delete button.
 *   testIdPrefix — used to generate stable test ids for the row + buttons.
 *   disabled — hides delete affordance entirely.
 *   className — applied to the outer wrapper.
 *
 * Usage:
 *   <SwipeRow onDelete={() => …} testIdPrefix={`conv-${c.id}`}>
 *     <button onClick={…}>row content</button>
 *   </SwipeRow>
 */
export default function SwipeRow({
  children,
  onDelete,
  testIdPrefix = "swipe-row",
  disabled = false,
  className = "",
}) {
  const [revealed, setRevealed] = useState(false);
  const startX = useRef(null);
  const dx = useRef(0);
  const longPressTimer = useRef(null);
  const moved = useRef(false);

  const reveal = () => setRevealed(true);
  const hide = () => setRevealed(false);

  const beginPress = (clientX) => {
    if (disabled) return;
    startX.current = clientX;
    dx.current = 0;
    moved.current = false;
    longPressTimer.current = setTimeout(() => {
      // Only fire long-press if the user has NOT swiped meaningfully.
      if (!moved.current) reveal();
    }, 600);
  };

  const movePress = (clientX) => {
    if (disabled || startX.current === null) return;
    dx.current = clientX - startX.current;
    if (Math.abs(dx.current) > 8) moved.current = true;
    // Cancel pending long-press once swipe starts
    if (Math.abs(dx.current) > 8 && longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const endPress = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
    if (disabled) return;
    // Reveal on a clear left-swipe (more than 48px)
    if (dx.current <= -48) reveal();
    else if (dx.current >= 48) hide();
    startX.current = null;
    dx.current = 0;
  };

  // Touch handlers
  const onTouchStart = (e) => beginPress(e.touches[0].clientX);
  const onTouchMove  = (e) => movePress(e.touches[0].clientX);
  const onTouchEnd   = ()  => endPress();
  // Mouse handlers (desktop drag — long-press still works via mousedown timer)
  const onMouseDown  = (e) => beginPress(e.clientX);
  const onMouseMove  = (e) => {
    // Only track movement while a press is in progress (startX !== null)
    if (startX.current !== null) movePress(e.clientX);
  };
  const onMouseUp    = ()  => endPress();
  const onMouseLeave = ()  => endPress();

  return (
    <div
      className={`relative overflow-hidden select-none ${className}`}
      data-testid={`${testIdPrefix}-swipe`}
      data-revealed={revealed ? "true" : "false"}
    >
      {/* Underlay action — only mounted when revealed for accessibility */}
      <button
        type="button"
        onClick={() => {
          onDelete?.();
          hide();
        }}
        aria-label="Delete"
        className={`absolute right-0 top-0 h-full w-[84px] bg-red-600 hover:bg-red-500 text-white flex flex-col items-center justify-center gap-1 transition-opacity duration-200 ${
          revealed ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        data-testid={`${testIdPrefix}-delete-btn`}
      >
        <Trash2 className="w-5 h-5" />
        <span className="text-[10px] uppercase tracking-widest font-black">Delete</span>
      </button>
      <div
        className="transition-transform duration-200 ease-out bg-transparent"
        style={{ transform: revealed ? "translateX(-84px)" : "translateX(0)" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onClick={(e) => {
          // Swallow the click that ends a swipe gesture so the row's primary
          // action doesn't fire after a swipe.
          if (moved.current) {
            e.stopPropagation();
            e.preventDefault();
            moved.current = false;
          }
        }}
      >
        {children}
      </div>
    </div>
  );
}
